import { useState, useEffect } from 'react';
import { useConfig, useUpdateConfig } from '@/hooks/useConfig';

// ============================================
// Type Definitions for Admin-Editable Content
// ============================================
interface Project {
    id: string;
    title: string;
    badge: string;
    description: string;
    link: string;
    cta: string;
    launched: string;
    featured: boolean;
}

interface Event {
    id: string;
    title: string;
    location: string;
    date: string;
    type: 'past' | 'upcoming';
    role: string;
    promo?: string;
}

interface Testimonial {
    id: string;
    name: string;
    handle: string;
    quote: string;
    initials: string;
}

interface Community {
    id: string;
    name: string;
    platform: string;
    members: string;
}

// ============================================
// Default Fallback Data
// ============================================
const DEFAULT_PROJECTS: Project[] = [
    {
        id: 'slamnet',
        title: 'SlamNet',
        badge: '$SLAM',
        description: 'Web3 information platform aggregating crypto news, project data, and educational resources.',
        link: 'https://slamnet.xyz',
        cta: 'Explore Platform',
        launched: 'July 2024',
        featured: true,
    },
    {
        id: 'slamx',
        title: 'SlamX',
        badge: 'Resources',
        description: 'Community hub with exclusive affiliate deals, trading tools, and educational content.',
        link: 'https://slamx.io',
        cta: 'Access Hub',
        launched: 'May 2024',
        featured: false,
    },
    {
        id: 'inner-circle',
        title: 'Inner Circle',
        badge: 'Premium',
        description: 'Private Discord & Patreon community with portfolio insights, trade alerts, and direct access.',
        link: 'https://patreon.com/OscarRamos',
        cta: 'Join Now',
        launched: '2023',
        featured: false,
    },
];

const DEFAULT_EVENTS: Event[] = [
    {
        id: 'bitcoin-2025',
        title: 'Bitcoin 2025',
        location: 'Las Vegas',
        date: 'May 27-29, 2025',
        type: 'past',
        role: 'Affiliate Partner',
    },
    {
        id: 'token2049-singapore',
        title: 'Token2049',
        location: 'Singapore',
        date: 'Sep 18-19, 2024',
        type: 'past',
        role: 'Featured Creator',
    },
];

const DEFAULT_TESTIMONIALS: Testimonial[] = [
    {
        id: '1',
        name: 'Alex (Ghost)',
        handle: '@AlTNodef_X',
        quote: "Ayeee! I'm Alex — also known as Ghost!! Texas born & raised and been grinding crypto since 2024.",
        initials: 'AG',
    },
    {
        id: '2',
        name: 'JB',
        handle: '@JB3479',
        quote: 'JB from the UK. On this crypto ride since 2021. Main Admin for Oscar Ramos YouTube and Telegram.',
        initials: 'JB',
    },
];

const DEFAULT_COMMUNITIES: Community[] = [
    { id: 'cro', name: '$1 CRO Party', platform: 'Telegram', members: '10K+' },
    { id: 'shib', name: '100B SHIB Party', platform: 'Telegram', members: '15K+' },
];

// ============================================
// Helper: Generate ID
// ============================================
const generateId = () => Math.random().toString(36).substring(2, 9);

// ============================================
// Main ContentEditor Component
// ============================================
export default function ContentEditor() {
    const { rawConfig } = useConfig();
    const updateConfig = useUpdateConfig();

    // Local state for form
    const [tickerInput, setTickerInput] = useState('');
    const [tickerSymbols, setTickerSymbols] = useState<string[]>([]);
    const [content, setContent] = useState({
        heroHeadline: '',
        heroSubtitle: '',
        ctaText: '',
        telegramLink: '',
        twitterHandle: '',
    });

    // Array-based content
    const [projects, setProjects] = useState<Project[]>(DEFAULT_PROJECTS);
    const [events, setEvents] = useState<Event[]>(DEFAULT_EVENTS);
    const [testimonials, setTestimonials] = useState<Testimonial[]>(DEFAULT_TESTIMONIALS);
    const [communities, setCommunities] = useState<Community[]>(DEFAULT_COMMUNITIES);

    // Active tab
    const [activeTab, setActiveTab] = useState<'hero' | 'projects' | 'events' | 'testimonials' | 'communities'>('hero');

    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // Sync from config
    useEffect(() => {
        if (!rawConfig) return;

        if (rawConfig.tickerSymbols) {
            setTickerSymbols(rawConfig.tickerSymbols as string[]);
        }
        if (rawConfig.content) {
            const c = rawConfig.content as typeof content;
            setContent({
                heroHeadline: c.heroHeadline || '',
                heroSubtitle: c.heroSubtitle || '',
                ctaText: c.ctaText || '',
                telegramLink: c.telegramLink || '',
                twitterHandle: c.twitterHandle || '',
            });
        }
        if (rawConfig.projects) {
            setProjects(rawConfig.projects as Project[]);
        }
        if (rawConfig.events) {
            setEvents(rawConfig.events as Event[]);
        }
        if (rawConfig.testimonials) {
            setTestimonials(rawConfig.testimonials as Testimonial[]);
        }
        if (rawConfig.communities) {
            setCommunities(rawConfig.communities as Community[]);
        }
    }, [rawConfig]);

    const handleAddTicker = () => {
        const symbol = tickerInput.toUpperCase().trim();
        if (symbol && !tickerSymbols.includes(symbol)) {
            setTickerSymbols([...tickerSymbols, symbol]);
            setTickerInput('');
        }
    };

    const handleRemoveTicker = (symbol: string) => {
        setTickerSymbols(tickerSymbols.filter((s) => s !== symbol));
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveMessage('');

        try {
            await updateConfig.mutateAsync([
                { key: 'tickerSymbols', value: tickerSymbols },
                { key: 'content', value: content },
                { key: 'projects', value: projects },
                { key: 'events', value: events },
                { key: 'testimonials', value: testimonials },
                { key: 'communities', value: communities },
            ]);
            setSaveMessage('✓ Saved successfully!');
        } catch (error) {
            setSaveMessage('✗ Failed to save');
        } finally {
            setIsSaving(false);
            setTimeout(() => setSaveMessage(''), 3000);
        }
    };

    // Tab button style helper
    const tabClass = (tab: typeof activeTab) =>
        `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab
            ? 'bg-neon-green/20 text-neon-green border border-neon-green/30'
            : 'text-zinc-400 hover:text-white hover:bg-white/5'
        }`;

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-white font-display mb-2">
                        Content Editor
                    </h1>
                    <p className="text-zinc-400">
                        Manage your site content, projects, events, and testimonials.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    {saveMessage && (
                        <span className={saveMessage.includes('✓') ? 'text-neon-green' : 'text-red-500'}>
                            {saveMessage}
                        </span>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="btn-primary"
                    >
                        {isSaving ? 'SAVING...' : 'SAVE CHANGES'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
                <button className={tabClass('hero')} onClick={() => setActiveTab('hero')}>
                    Hero & Ticker
                </button>
                <button className={tabClass('projects')} onClick={() => setActiveTab('projects')}>
                    Projects ({projects.length})
                </button>
                <button className={tabClass('events')} onClick={() => setActiveTab('events')}>
                    Events ({events.length})
                </button>
                <button className={tabClass('testimonials')} onClick={() => setActiveTab('testimonials')}>
                    Testimonials ({testimonials.length})
                </button>
                <button className={tabClass('communities')} onClick={() => setActiveTab('communities')}>
                    Communities ({communities.length})
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'hero' && (
                <>
                    {/* Ticker Manager */}
                    <div className="card mb-6">
                        <h2 className="text-xl font-bold text-white mb-4">Ticker Symbols</h2>
                        <p className="text-zinc-400 text-sm mb-4">
                            Add crypto symbols to display in the scrolling ticker.
                        </p>

                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                value={tickerInput}
                                onChange={(e) => setTickerInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddTicker()}
                                placeholder="BTC, ETH, SOL..."
                                className="flex-1 bg-void border border-white/10 rounded-lg px-4 py-2 text-white placeholder:text-zinc-600 focus:border-neon-green/50 focus:outline-none"
                            />
                            <button onClick={handleAddTicker} className="btn-ghost">
                                Add
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {tickerSymbols.map((symbol) => (
                                <span
                                    key={symbol}
                                    className="inline-flex items-center gap-2 px-3 py-1 bg-void rounded-full text-sm border border-white/10"
                                >
                                    <span className="text-white">{symbol}</span>
                                    <button
                                        onClick={() => handleRemoveTicker(symbol)}
                                        className="text-zinc-500 hover:text-red-500"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Content Fields */}
                    <div className="card">
                        <h2 className="text-xl font-bold text-white mb-4">Hero Content</h2>

                        <div className="space-y-4">
                            <FormField
                                label="Headline"
                                value={content.heroHeadline}
                                onChange={(v) => setContent({ ...content, heroHeadline: v })}
                                placeholder="Alpha Calls Only"
                            />

                            <FormField
                                label="Subtitle"
                                value={content.heroSubtitle}
                                onChange={(v) => setContent({ ...content, heroSubtitle: v })}
                                placeholder="100x or nothing. Join the degen elite."
                            />

                            <FormField
                                label="CTA Button Text"
                                value={content.ctaText}
                                onChange={(v) => setContent({ ...content, ctaText: v })}
                                placeholder="APE IN NOW"
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    label="Telegram Link"
                                    value={content.telegramLink}
                                    onChange={(v) => setContent({ ...content, telegramLink: v })}
                                    placeholder="https://t.me/..."
                                />

                                <FormField
                                    label="Twitter Handle"
                                    value={content.twitterHandle}
                                    onChange={(v) => setContent({ ...content, twitterHandle: v })}
                                    placeholder="@handle"
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'projects' && (
                <ProjectsManager projects={projects} setProjects={setProjects} />
            )}

            {activeTab === 'events' && (
                <EventsManager events={events} setEvents={setEvents} />
            )}

            {activeTab === 'testimonials' && (
                <TestimonialsManager testimonials={testimonials} setTestimonials={setTestimonials} />
            )}

            {activeTab === 'communities' && (
                <CommunitiesManager communities={communities} setCommunities={setCommunities} />
            )}
        </div>
    );
}

// ============================================
// Projects Manager
// ============================================
function ProjectsManager({
    projects,
    setProjects,
}: {
    projects: Project[];
    setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
}) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<Partial<Project>>({});

    const handleAdd = () => {
        const newProject: Project = {
            id: generateId(),
            title: 'New Project',
            badge: 'New',
            description: 'Project description',
            link: 'https://',
            cta: 'Learn More',
            launched: new Date().getFullYear().toString(),
            featured: false,
        };
        setProjects([...projects, newProject]);
        setEditingId(newProject.id);
        setDraft(newProject);
    };

    const handleSaveEdit = () => {
        if (!editingId) return;
        setProjects(projects.map((p) => (p.id === editingId ? { ...p, ...draft } : p)));
        setEditingId(null);
        setDraft({});
    };

    const handleDelete = (id: string) => {
        setProjects(projects.filter((p) => p.id !== id));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Projects</h2>
                <button onClick={handleAdd} className="btn-ghost">
                    + Add Project
                </button>
            </div>

            {projects.map((project) => (
                <div key={project.id} className="card">
                    {editingId === project.id ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    label="Title"
                                    value={draft.title || ''}
                                    onChange={(v) => setDraft({ ...draft, title: v })}
                                    placeholder="Project Title"
                                />
                                <FormField
                                    label="Badge"
                                    value={draft.badge || ''}
                                    onChange={(v) => setDraft({ ...draft, badge: v })}
                                    placeholder="$TOKEN or Label"
                                />
                            </div>
                            <FormField
                                label="Description"
                                value={draft.description || ''}
                                onChange={(v) => setDraft({ ...draft, description: v })}
                                placeholder="Project description..."
                                multiline
                            />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    label="Link"
                                    value={draft.link || ''}
                                    onChange={(v) => setDraft({ ...draft, link: v })}
                                    placeholder="https://..."
                                />
                                <FormField
                                    label="CTA Text"
                                    value={draft.cta || ''}
                                    onChange={(v) => setDraft({ ...draft, cta: v })}
                                    placeholder="Explore Now"
                                />
                                <FormField
                                    label="Launched"
                                    value={draft.launched || ''}
                                    onChange={(v) => setDraft({ ...draft, launched: v })}
                                    placeholder="July 2024"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id={`featured-${project.id}`}
                                    checked={draft.featured || false}
                                    onChange={(e) => setDraft({ ...draft, featured: e.target.checked })}
                                    className="w-4 h-4"
                                />
                                <label htmlFor={`featured-${project.id}`} className="text-white text-sm">
                                    Featured Project
                                </label>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleSaveEdit} className="btn-primary text-sm">
                                    Save
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingId(null);
                                        setDraft({});
                                    }}
                                    className="btn-ghost text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <h3 className="text-lg font-bold text-white">{project.title}</h3>
                                    <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-300 rounded">
                                        {project.badge}
                                    </span>
                                    {project.featured && (
                                        <span className="px-2 py-0.5 text-xs bg-neon-green/20 text-neon-green rounded">
                                            Featured
                                        </span>
                                    )}
                                </div>
                                <p className="text-zinc-400 text-sm mb-2">{project.description}</p>
                                <p className="text-zinc-500 text-xs">
                                    {project.link} • Launched {project.launched}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setEditingId(project.id);
                                        setDraft(project);
                                    }}
                                    className="text-zinc-400 hover:text-white text-sm"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(project.id)}
                                    className="text-zinc-400 hover:text-red-500 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ============================================
// Events Manager
// ============================================
function EventsManager({
    events,
    setEvents,
}: {
    events: Event[];
    setEvents: React.Dispatch<React.SetStateAction<Event[]>>;
}) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<Partial<Event>>({});

    const handleAdd = () => {
        const newEvent: Event = {
            id: generateId(),
            title: 'New Event',
            location: 'TBD',
            date: 'TBD',
            type: 'upcoming',
            role: 'Attendee',
        };
        setEvents([...events, newEvent]);
        setEditingId(newEvent.id);
        setDraft(newEvent);
    };

    const handleSaveEdit = () => {
        if (!editingId) return;
        setEvents(events.map((e) => (e.id === editingId ? { ...e, ...draft } as Event : e)));
        setEditingId(null);
        setDraft({});
    };

    const handleDelete = (id: string) => {
        setEvents(events.filter((e) => e.id !== id));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Events</h2>
                <button onClick={handleAdd} className="btn-ghost">
                    + Add Event
                </button>
            </div>

            {events.map((event) => (
                <div key={event.id} className="card">
                    {editingId === event.id ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    label="Title"
                                    value={draft.title || ''}
                                    onChange={(v) => setDraft({ ...draft, title: v })}
                                    placeholder="Event Name"
                                />
                                <FormField
                                    label="Location"
                                    value={draft.location || ''}
                                    onChange={(v) => setDraft({ ...draft, location: v })}
                                    placeholder="City, Country"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    label="Date"
                                    value={draft.date || ''}
                                    onChange={(v) => setDraft({ ...draft, date: v })}
                                    placeholder="May 27-29, 2025"
                                />
                                <FormField
                                    label="Role"
                                    value={draft.role || ''}
                                    onChange={(v) => setDraft({ ...draft, role: v })}
                                    placeholder="Speaker, Partner, etc."
                                />
                                <FormField
                                    label="Promo Code (optional)"
                                    value={draft.promo || ''}
                                    onChange={(v) => setDraft({ ...draft, promo: v })}
                                    placeholder="OSCAR10"
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-white text-sm">
                                    <input
                                        type="radio"
                                        name={`type-${event.id}`}
                                        checked={draft.type === 'upcoming'}
                                        onChange={() => setDraft({ ...draft, type: 'upcoming' })}
                                    />
                                    Upcoming
                                </label>
                                <label className="flex items-center gap-2 text-white text-sm">
                                    <input
                                        type="radio"
                                        name={`type-${event.id}`}
                                        checked={draft.type === 'past'}
                                        onChange={() => setDraft({ ...draft, type: 'past' })}
                                    />
                                    Past
                                </label>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleSaveEdit} className="btn-primary text-sm">
                                    Save
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingId(null);
                                        setDraft({});
                                    }}
                                    className="btn-ghost text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <h3 className="text-lg font-bold text-white">{event.title}</h3>
                                    <span
                                        className={`px-2 py-0.5 text-xs rounded ${event.type === 'upcoming'
                                                ? 'bg-neon-green/20 text-neon-green'
                                                : 'bg-zinc-800 text-zinc-400'
                                            }`}
                                    >
                                        {event.type}
                                    </span>
                                </div>
                                <p className="text-zinc-400 text-sm">
                                    {event.location} • {event.date}
                                </p>
                                <p className="text-zinc-500 text-xs mt-1">{event.role}</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setEditingId(event.id);
                                        setDraft(event);
                                    }}
                                    className="text-zinc-400 hover:text-white text-sm"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(event.id)}
                                    className="text-zinc-400 hover:text-red-500 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ============================================
// Testimonials Manager
// ============================================
function TestimonialsManager({
    testimonials,
    setTestimonials,
}: {
    testimonials: Testimonial[];
    setTestimonials: React.Dispatch<React.SetStateAction<Testimonial[]>>;
}) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<Partial<Testimonial>>({});

    const handleAdd = () => {
        const newTestimonial: Testimonial = {
            id: generateId(),
            name: 'Community Member',
            handle: '@handle',
            quote: 'Add testimonial quote here...',
            initials: 'CM',
        };
        setTestimonials([...testimonials, newTestimonial]);
        setEditingId(newTestimonial.id);
        setDraft(newTestimonial);
    };

    const handleSaveEdit = () => {
        if (!editingId) return;
        setTestimonials(testimonials.map((t) => (t.id === editingId ? { ...t, ...draft } : t)));
        setEditingId(null);
        setDraft({});
    };

    const handleDelete = (id: string) => {
        setTestimonials(testimonials.filter((t) => t.id !== id));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Testimonials</h2>
                <button onClick={handleAdd} className="btn-ghost">
                    + Add Testimonial
                </button>
            </div>

            {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="card">
                    {editingId === testimonial.id ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    label="Name"
                                    value={draft.name || ''}
                                    onChange={(v) => setDraft({ ...draft, name: v })}
                                    placeholder="Full Name"
                                />
                                <FormField
                                    label="Handle"
                                    value={draft.handle || ''}
                                    onChange={(v) => setDraft({ ...draft, handle: v })}
                                    placeholder="@twitter"
                                />
                                <FormField
                                    label="Initials"
                                    value={draft.initials || ''}
                                    onChange={(v) => setDraft({ ...draft, initials: v.toUpperCase().slice(0, 2) })}
                                    placeholder="AB"
                                />
                            </div>
                            <FormField
                                label="Quote"
                                value={draft.quote || ''}
                                onChange={(v) => setDraft({ ...draft, quote: v })}
                                placeholder="Testimonial quote..."
                                multiline
                            />
                            <div className="flex gap-2">
                                <button onClick={handleSaveEdit} className="btn-primary text-sm">
                                    Save
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingId(null);
                                        setDraft({});
                                    }}
                                    className="btn-ghost text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-start justify-between">
                            <div className="flex gap-4">
                                <div className="w-12 h-12 rounded-full bg-purple-500/30 flex items-center justify-center text-purple-300 font-bold flex-shrink-0">
                                    {testimonial.initials}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-white font-medium">{testimonial.name}</h3>
                                        <span className="text-zinc-500 text-sm">{testimonial.handle}</span>
                                    </div>
                                    <p className="text-zinc-400 text-sm line-clamp-2">"{testimonial.quote}"</p>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <button
                                    onClick={() => {
                                        setEditingId(testimonial.id);
                                        setDraft(testimonial);
                                    }}
                                    className="text-zinc-400 hover:text-white text-sm"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(testimonial.id)}
                                    className="text-zinc-400 hover:text-red-500 text-sm"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ============================================
// Communities Manager
// ============================================
function CommunitiesManager({
    communities,
    setCommunities,
}: {
    communities: Community[];
    setCommunities: React.Dispatch<React.SetStateAction<Community[]>>;
}) {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<Partial<Community>>({});

    const handleAdd = () => {
        const newCommunity: Community = {
            id: generateId(),
            name: 'New Community',
            platform: 'Telegram',
            members: '1K+',
        };
        setCommunities([...communities, newCommunity]);
        setEditingId(newCommunity.id);
        setDraft(newCommunity);
    };

    const handleSaveEdit = () => {
        if (!editingId) return;
        setCommunities(communities.map((c) => (c.id === editingId ? { ...c, ...draft } : c)));
        setEditingId(null);
        setDraft({});
    };

    const handleDelete = (id: string) => {
        setCommunities(communities.filter((c) => c.id !== id));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Communities</h2>
                <button onClick={handleAdd} className="btn-ghost">
                    + Add Community
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {communities.map((community) => (
                    <div key={community.id} className="card">
                        {editingId === community.id ? (
                            <div className="space-y-3">
                                <FormField
                                    label="Name"
                                    value={draft.name || ''}
                                    onChange={(v) => setDraft({ ...draft, name: v })}
                                    placeholder="Community Name"
                                />
                                <FormField
                                    label="Platform"
                                    value={draft.platform || ''}
                                    onChange={(v) => setDraft({ ...draft, platform: v })}
                                    placeholder="Telegram, Discord, etc."
                                />
                                <FormField
                                    label="Members"
                                    value={draft.members || ''}
                                    onChange={(v) => setDraft({ ...draft, members: v })}
                                    placeholder="10K+"
                                />
                                <div className="flex gap-2">
                                    <button onClick={handleSaveEdit} className="btn-primary text-sm">
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingId(null);
                                            setDraft({});
                                        }}
                                        className="btn-ghost text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-white font-medium">{community.name}</h3>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => {
                                                setEditingId(community.id);
                                                setDraft(community);
                                            }}
                                            className="text-zinc-500 hover:text-white text-xs"
                                        >
                                            ✎
                                        </button>
                                        <button
                                            onClick={() => handleDelete(community.id)}
                                            className="text-zinc-500 hover:text-red-500 text-xs"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-zinc-400">{community.platform}</span>
                                    <span className="text-zinc-600">•</span>
                                    <span className="text-neon-green">{community.members}</span>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================
// Form Field Component
// ============================================
function FormField({
    label,
    value,
    onChange,
    placeholder,
    multiline = false,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    multiline?: boolean;
}) {
    const baseClasses =
        'w-full bg-void border border-white/10 rounded-lg px-4 py-2 text-white placeholder:text-zinc-600 focus:border-neon-green/50 focus:outline-none';

    return (
        <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">{label}</label>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`${baseClasses} min-h-[100px] resize-y`}
                    rows={3}
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={baseClasses}
                />
            )}
        </div>
    );
}
